import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';

import { ContractSignWebSocket } from '@repo/customer-registration-kit/contract-sign/components/contract-sign-websocket/contract-sign-websocket';
import { Button } from '@repo/design-system-kit/components/button/button';
import { Checkbox } from '@repo/design-system-kit/components/checkbox/checkbox';
import { Disclaimer } from '@repo/design-system-kit/components/disclaimer/disclaimer';
import { formatMoney } from '@repo/design-system-kit/utils/format-money';
import { shouldSendToSentry } from '@repo/runtime-kit/utils/should-send-to-sentry';
import { webviewManager } from '@repo/runtime-kit/webview';
import { dropTask, forever } from 'ember-concurrency';
import window from 'ember-window-mock';

import { embedOnboardingURL } from 'register-partners-js/constants/hosts';
import { ContentWrapper } from 'register-partners-js/react/components/content-wrapper';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';
import { PricingError } from 'register-partners-js/react/components/pricing-error';
import { postOrganizationRegisterV2 } from 'register-partners-js/react/register-organization/queries';
import {
  getDougsDisplayName,
  isDougsCobrandingEnabled,
} from 'register-partners-js/utils/is-cobranding-dougs';
import { ignoreCancelation } from 'register-partners-js/utils/ignore-cancelation';
import { shouldRedirectToEmbed } from 'register-partners-js/utils/should-redirect-to-embed';

import styles from './styles.module.css';

export default class RegistrationOrderSummaryController extends Controller {
  mainLayout = MainLayout;
  contractSignWebSocket = ContractSignWebSocket;

  styles = styles;

  @service checkout;
  @service router;
  @service segment;
  @service currentUser;

  @service deviceManager;
  @service toastFlashMessages;
  @service intl;
  @service pricingService;
  @service sentry;
  @service userManager;

  @tracked contractApproved = false;
  @tracked showContractError = false;
  @tracked currentPricePlan;

  @tracked selectedRecurrence;
  @tracked estimation;
  @tracked contractSignWebSocketEnabled = false;

  get websocketToken() {
    return this.userManager.websocketToken;
  }

  get currentUserId() {
    return this.userManager.currentUser?.id;
  }

  pricingError = PricingError;
  checkbox = Checkbox;
  button = Button;
  contentWrapper = ContentWrapper;
  disclaimerBlock = Disclaimer.Block;

  get title() {
    return this.intl.t('sign_up.contract.deposit.title');
  }

  get buttonText() {
    if (this.deviceManager.isMobile) {
      return this.intl.t('generic.confirm.cta');
    }

    if (this.selectedPricePlanRecurrence === 'annual') {
      return this.intl.t('check_out.regular-flow.confirm.annual', {
        planName: this.capitalizedBrandName,
      });
    }

    return this.intl.t('check_out.regular-flow.confirm.monthly', {
      planName: this.capitalizedBrandName,
    });
  }

  getPricePlanTask = dropTask(async () => {
    let { organization, registration } = this.model;

    let [selectedProducts, pricePlans] = await Promise.all([
      this.checkout.fetchPricePlanSelectedTask.perform(organization.id),
      this.checkout.fetchPricePlansForExistingCompanyTask.perform({
        legalCountry: organization.legalCountry,
        partnerName: registration.partnerName?.toLowerCase(),
      }),
    ]);

    let selectedPricePlan = selectedProducts.selected_products.find(
      ({ product_kind: kind }) => kind === 'core'
    );

    this.selectedRecurrence = selectedPricePlan.recurrence;

    this.currentPricePlan = pricePlans.products.find(
      pricePlan => pricePlan.id === selectedPricePlan.product_id
    );

    this.estimation = await this.checkout.fetchPriceEstimateForExistingCompanyTask.perform({
      legalCountry: selectedProducts.legal_country,
      productId: selectedPricePlan.product_id,
      recurrence: selectedPricePlan.recurrence,
    });
  });

  get selectedPricePlanRecurrence() {
    return this.selectedRecurrence;
  }

  get pricePlan() {
    return this.currentPricePlan;
  }

  get termsAndConditionsError() {
    return this.intl.t('sign_up.contract.deposit.terms-and-conditions.error');
  }

  get regularFlowListItem1() {
    let partnerName = this.model.registration.partnerName;
    if (isDougsCobrandingEnabled(partnerName)) {
      return this.intl.t('check_out.regular-flow.list.item-1.partner-name', {
        partner: getDougsDisplayName(partnerName),
      });
    }
    return this.intl.t('check_out.regular-flow.list.item-1');
  }

  get promoOffer() {
    return this.estimation?.product?.promo_discount;
  }

  // oxlint-disable-next-line getter-return
  get promoPriceValue() {
    if (this.promoOffer) {
      return this.promoOffer.discounted_price.value;
    }
  }

  get regularPrice() {
    if (this.selectedPricePlanRecurrence === 'annual' && this.promoOffer) {
      return this.currentPricePlan.annual_price.per_year_amount.value;
    }
    if (this.selectedPricePlanRecurrence === 'annual') {
      return this.currentPricePlan.monthly_price.per_year_amount.value;
    }
    return this.estimation?.product?.price_amount.value;
  }

  get paymentInvoiceAmount() {
    if (this.promoOffer) {
      return this.promoPriceValue;
    }
    return this.estimation?.total_amount?.value;
  }

  // oxlint-disable-next-line getter-return
  get disclaimerText() {
    if (!this.promoOffer) {
      return;
    }

    let sharedVariables = {
      regular_amount: formatMoney({
        amount: this.regularPrice,
        token: 'money-symbol-compact',
      }),
      discounted_amount: formatMoney({
        amount: this.promoPriceValue,
        token: 'money-symbol-compact',
      }),
      name: this.capitalizedBrandName,
    };
    if (this.selectedPricePlanRecurrence === 'annual') {
      return this.intl.t(
        'price_plan.deposit-capital.recap.disclaimer.promo.annual',
        sharedVariables
      );
    } else if (this.selectedPricePlanRecurrence === 'monthly') {
      return this.intl.t('order-new.disclaimer.monthly-promo', {
        ...sharedVariables,
        discount_duration: this.promoOffer.month_duration,
      });
    }
  }

  @action
  toggleContractCheckbox() {
    this.segment.track('onboarding_cta_termsandconditions');

    this.contractApproved = !this.contractApproved;
    this.showContractError = false;
  }

  registerTask = dropTask(async organization => {
    await this.registerV2Task.perform(organization).catch(ignoreCancelation);
  });

  registerV2Task = dropTask(
    waitFor(async organization => {
      try {
        await postOrganizationRegisterV2(organization.id);
      } catch (error) {
        if (shouldSendToSentry(error)) {
          this.entry.captureException(error);
        }
        this.toastFlashMessages.toastError(this.intl.t('errors.internal-server-error'));
        return;
      }

      this.contractSignWebSocketEnabled = true;

      await forever;
    })
  );

  @action
  handleContractSignSuccess() {
    this.registerV2Task.cancelAll();
    let { organization } = this.model;
    this.trackContractSigned(organization);
    if (shouldRedirectToEmbed(this.model.registration, organization)) {
      this.redirectToEmbedOnboarding(this.model.registration.id);
      return;
    }
    this.router.transitionTo('registration.next-steps');
  }

  @action
  handleContractSignValidationError() {
    this.registerV2Task.cancelAll();
    this.toastFlashMessages.toastError(this.intl.t('errors.internal-server-error'));
  }

  @action
  handleContractSignTimeout() {
    this.registerV2Task.cancelAll();
    this.router.transitionTo('registration.pending-contract');
  }

  confirmTask = dropTask(
    waitFor(async () => {
      if (!this.contractApproved) {
        this.showContractError = true;

        return;
      }

      await this.registerTask.perform(this.model.organization);
    })
  );

  trackContractSigned(organization) {
    let pricePlan = this.currentPricePlan;

    let recurrence = this.selectedPricePlanRecurrence;

    let revenue;
    let yearlyRevenue;
    let currency;
    let product_id;

    let { tracking_details } = pricePlan;
    revenue =
      recurrence === 'annual'
        ? tracking_details.annual_plan_conversion_value
        : tracking_details.monthly_plan_conversion_value;

    yearlyRevenue =
      recurrence === 'annual'
        ? tracking_details.annual_plan_yearly_revenue
        : tracking_details.monthly_plan_yearly_revenue;

    currency = tracking_details.conversion_currency;
    product_id = pricePlan.id;

    let owner = organization.get('owner');

    this.segment.track('onboarding_partner_contract_signed', {
      recurrence,
      revenue,
      command: 'onboarding_confirmation',
      legal_country: organization.get('legalCountry'),
      currency,
      ec_order_id: this.currentUser.user?.segmentId,
      ec_order_revenue: yearlyRevenue,
      ec_product: [
        {
          product_id,
          product_name: pricePlan.code,
          product_brand: 'Qonto',
          product_category: 'Existing',
          product_category2: pricePlan.lineup,
          product_price: yearlyRevenue,
          product_quantity: 1,
        },
      ],
      email: this.currentUser.user?.email,
      first_name: owner.get('firstName'),
      last_name: owner.get('lastName'),
      phone: owner.get('phoneNumber'),
      under_registration: false,
      order_id: organization.id,
      organization_id: organization.id,
      original_price_plan_code: this.currentPricePlan.code,
      is_webview: webviewManager.isWebview,
      partner_name: this.model.registration.partnerName,
    });

    this.segment.track(`onboarding_contract_${this.currentPricePlan.code}_signed`);
  }

  @action
  handleGoBack() {
    return this.router.transitionTo('registration.price-plans', this.model.registration.id);
  }

  get capitalizedBrandName() {
    return this.currentPricePlan?.brand_name;
  }

  redirectToEmbedOnboarding(registrationId) {
    window.location.href = `${embedOnboardingURL}/${registrationId}`;
  }
}
